import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { FeatherSearch, FeatherX } from '@subframe/core';
import { useGlobalSearch, SearchResult } from '../hooks/useGlobalSearch';
import './styles/GlobalSearchBar.css';
import { is3DModelUrl } from '../utils/media';

interface GlobalSearchBarProps {
  className?: string;
  placeholder?: string;
  /** If true, shows only the search icon on mobile which expands to full search */
  mobileCollapsible?: boolean;
}

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  className = '',
  placeholder = 'Search collections, wallets, NFTs...',
  mobileCollapsible = false,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // For mobile collapsed state
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { results, isSearching, isEmpty, debouncedQuery } = useGlobalSearch(query, {
    debounceMs: 300,
    limit: 12,
  });

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [results]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        // Also collapse on mobile if clicking outside
        if (mobileCollapsible && !query) {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileCollapsible, query]);

  // Focus input when expanding on mobile
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleSelect = useCallback(
    (item: SearchResult) => {
      setIsOpen(false);
      setQuery('');
      setHighlightedIndex(-1);
      if (mobileCollapsible) {
        setIsExpanded(false);
      }

      switch (item.type) {
        case 'collection':
          navigate(`/collections/${item.slug || item.id}`);
          break;
        case 'wallet':
          navigate(`/wallets/${item.id}`);
          break;
        case 'nft':
          navigate(`/nfts/${item.id}`);
          break;
      }
    },
    [navigate, mobileCollapsible]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        if (mobileCollapsible && !query) {
          setIsExpanded(false);
        }
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (mobileCollapsible && !query) {
          setIsExpanded(false);
        }
        inputRef.current?.blur();
        break;
    }
  };

  const handleFocus = () => {
    if (query.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length >= 2);
  };

  const clearSearch = () => {
    setQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleMobileSearchClick = () => {
    setIsExpanded(true);
  };

  const handleMobileClose = () => {
    setQuery('');
    setIsOpen(false);
    setIsExpanded(false);
  };

  const showDropdown = isOpen && debouncedQuery.length >= 2;

  // Get icon for result type
  const getTypeIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'collection':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14-7H5a2 2 0 00-2 2v16a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
          </svg>
        );
      case 'wallet':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        );
      case 'nft':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  // Get type label
  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'collection': return 'Collection';
      case 'wallet': return 'Wallet';
      case 'nft': return 'NFT';
    }
  };

  // Mobile collapsed view - just show search icon
  if (mobileCollapsible && !isExpanded) {
    return (
      <button
        type="button"
        onClick={handleMobileSearchClick}
        className="global-search-mobile-trigger"
        aria-label="Open search"
      >
        <FeatherSearch className="global-search-mobile-trigger-icon" />
      </button>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`global-search-container ${mobileCollapsible ? 'mobile-expanded' : ''} ${className}`}
    >
      <div className="global-search-wrapper">
        {/* Search Icon / Loading Spinner */}
        <div className="global-search-icon">
          {isSearching ? (
            <svg
              className="global-search-spinner"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <FeatherSearch className="global-search-icon-svg" />
          )}
        </div>

        {/* Input Field */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="global-search-input"
          aria-label="Global search"
          aria-autocomplete="list"
          aria-controls="global-search-dropdown"
          aria-expanded={showDropdown}
        />

        {/* Clear/Close Button */}
        {(query || mobileCollapsible) && (
          <button
            type="button"
            onClick={mobileCollapsible && !query ? handleMobileClose : clearSearch}
            className="global-search-clear"
            aria-label={mobileCollapsible && !query ? 'Close search' : 'Clear search'}
          >
            <FeatherX />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div id="global-search-dropdown" className="global-search-dropdown" role="listbox">
          {/* Loading State */}
          {isSearching && results.length === 0 && (
            <div className="global-search-loading">Searching...</div>
          )}

          {/* Results */}
          {results.length > 0 && (
            <div className="global-search-results">
              {results.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}`}
                  type="button"
                  onClick={() => handleSelect(result)}
                  className={`global-search-item ${highlightedIndex === index ? 'highlighted' : ''}`}
                  role="option"
                  aria-selected={highlightedIndex === index}
                >
                  {/* Image or Icon */}
                  <div className="global-search-item-image">
                    {is3DModelUrl(result.image_url) ? (
                      <div className={`global-search-item-placeholder ${result.type}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '16px' }}>🧊</span>
                      </div>
                    ) : result.image_url ? (
                      <img src={result.image_url} alt={result.name} />
                    ) : (
                      <div className={`global-search-item-placeholder ${result.type}`}>
                        {getTypeIcon(result.type)}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="global-search-item-content">
                    <div className="global-search-item-name">
                      {result.name}
                      {result.is_verified && (
                        <span className="global-search-verified" title="Verified">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                          </svg>
                        </span>
                      )}
                    </div>
                    <div className="global-search-item-subtitle">
                      {result.subtitle}
                    </div>
                  </div>

                  {/* Type Badge */}
                  <div className={`global-search-type-badge ${result.type}`}>
                    {getTypeLabel(result.type)}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {!isSearching && isEmpty && debouncedQuery.length >= 2 && (
            <div className="global-search-no-results">
              No results found for "{debouncedQuery}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};
